import {
  LoanProgram,
  RateDetails,
  ProductGroup,
} from '@frontend/pricing-search';
import { DeepPartial, parseNumber } from '@frontend/utils';
export interface Target {
  isTargetPrice: boolean;
  value?: number;
}
export interface SortableProperties {
  delta: number;
  tPrimary: number;
  tSecondary: number;
  discountPoints?: number;
  target?: SortableRateDetails;
  price?: number;
  rate?: number;
  indexRow?: any;
  productGroupID?: number;
}
type SortableLoanProgram = DeepPartial<LoanProgram> & SortableProperties;
type SortableRateDetails = DeepPartial<RateDetails> & SortableProperties;
type SortableProductGroup = DeepPartial<ProductGroup> & SortableProperties;

export function makeSortDelta(isZeroBasedPricing, isTargetPrice) {
  const THRESH = isZeroBasedPricing ? 50 : 100;
  return function sortDelta(a, b) {
    // first check error state
    if (a.deleted !== 0 && b.deleted !== 0) return a.deleted - b.deleted;
    if (a.deleted !== 0) return 1;
    if (b.deleted !== 0) return -1;

    // Target Rate scenarios (isTargetPrice === false)
    if (!isTargetPrice) {
      // For hundred-based target rate: delta is negative price, so eligible items have delta < 0
      // For zero-based target rate: delta is price directly
      const aEligible = isZeroBasedPricing ? a.delta < THRESH : a.delta < 0;
      const bEligible = isZeroBasedPricing ? b.delta < THRESH : b.delta < 0;

      if (isZeroBasedPricing) {
        // Zero-based target rate:
        // Both eligible: sort by price asc (lowest/most negative price first = best), then rate asc
        if (aEligible && bEligible) {
          if (a.tSecondary !== b.tSecondary) return a.tSecondary - b.tSecondary;
          return a.tPrimary - b.tPrimary;
        }
        // Only a is eligible
        if (aEligible) return -1;
        // Only b is eligible
        if (bEligible) return 1;
        // Neither eligible: sort by rate ascending (closest to target first)
        if (a.tPrimary === b.tPrimary) return a.tSecondary - b.tSecondary;
        return a.tPrimary - b.tPrimary;
      }

      // Hundred-based target rate: 
      // Both eligible: sort by price desc (highest price first)
      if (aEligible && bEligible) {
        // tSecondary is price for target rate
        // Sort by price descending (highest first)
        if (a.tSecondary !== b.tSecondary) return b.tSecondary - a.tSecondary;
        // If prices match, sort by rate ascending
        return a.tPrimary - b.tPrimary;
      }
      // Only a is eligible
      if (aEligible) return -1;
      // Only b is eligible
      if (bEligible) return 1;
      // Neither eligible: sort by rate ascending (closest to target first)
      if (a.tPrimary === b.tPrimary) return b.tSecondary - a.tSecondary;
      return a.tPrimary - b.tPrimary;
    }

    // Both programs have pricing at or above target
    const bothAtOrAbove = a.delta < THRESH && b.delta < THRESH;

    // Target Price scenarios (isTargetPrice === true)
    if (bothAtOrAbove) {
      // Hundred-based: if rates match, sort price desc
      if (!isZeroBasedPricing && a.tSecondary === b.tSecondary) {
        return b.tPrimary - a.tPrimary;
      }
      // Otherwise sort secondary (rate) asc, then primary (price) asc
      if (a.tSecondary === b.tSecondary) return a.tPrimary - b.tPrimary;
      return a.tSecondary - b.tSecondary;
    }
    // only program a has target pricing
    if (a.delta < THRESH) return -1;
    // only program b has target pricing
    if (b.delta < THRESH) return 1;
    // both programs are below target
    if (a.tPrimary === b.tPrimary) return a.tSecondary - b.tSecondary;
    return a.tPrimary - b.tPrimary;
  };
}
export function calcDeltas(
  aProgs: SortableLoanProgram[],
  target: Target,
  isZeroBasedPricing: boolean,
): SortableLoanProgram[] {
  // get the best delta for each rate
  const hRate: Record<string, SortableRateDetails> = {};
  const v = Number(target.value ?? 0);
  const t = target.isTargetPrice
    ? isZeroBasedPricing && v === 100
      ? 0
      : 100 - v
    : v;
  let od: number;
  let r: SortableRateDetails;
  let prim: number;
  let sec: number;
  aProgs.forEach((prog) => {
    const aRates = prog.rateDetails;
    if (aRates) {
      const l = aRates.length;
      // Check if all primary values (price for target pricing, rate for target rate) are below target
      // For target rate: check if all rates are above the target (no eligible rates)
      // For target price: check if all prices are below the target
      const allPrimaryValuesBelowTarget = aRates.every((rate) => {
        if (!rate) return false;
        const primaryValue = target.isTargetPrice
          ? typeof rate.price === 'number'
            ? rate.price
            : null
          : typeof rate.rate === 'number'
          ? rate.rate
          : null;
        if (primaryValue === null) return false;
        // For target rate (both zero and hundred based): eligible if rate <= target, so "below target" means rate > target
        // For target price: eligible if price >= target (hundred) or price <= target (zero)
        if (!target.isTargetPrice) {
          return primaryValue > t; // rate > target means not eligible
        }
        return !(isZeroBasedPricing
          ? t - primaryValue >= 0
          : primaryValue >= t);
      });
      // Check if all primary values are above target for zero based pricing
      let allPrimaryValuesAboveTarget = false;
      if (isZeroBasedPricing && target.isTargetPrice) {
        allPrimaryValuesAboveTarget = aRates.every((rate) => {
          if (!rate) return false;
          let primaryValue: number | null = null;
          if (typeof rate.price === 'number') {
            primaryValue = rate.price;
          }
          if (primaryValue === null) return false;
          return t - primaryValue < 0;
        });
      }

      for (let i = 0; i < l; i += 1) {
        r = aRates[i] as SortableRateDetails;
        od = hRate[prog.programID as number]?.delta ?? 999;
        prim = (target.isTargetPrice ? r.price : r.rate) as number;
        sec = (target.isTargetPrice ? r.rate : r.price) as number;

        // Eligibility check: rate/price meets target threshold
        // For target rate (both zero and hundred based): rate <= target is eligible
        // For target price zero-based: price <= target is eligible (t - prim >= 0)
        // For target price hundred-based: price >= target is eligible (prim >= t)
        const isEligible = target.isTargetPrice
          ? isZeroBasedPricing
            ? t - prim >= 0
            : prim >= t
          : t - prim >= 0; // For target rate: rate <= target (same as legacy)

        if (isEligible) {
          // For hundred-based target rate: use negative price so highest price wins (min delta)
          // For all other cases: use secondary value directly
          if (!target.isTargetPrice && !isZeroBasedPricing) {
            r.delta = -sec; // Negative price: higher price = lower delta = wins
          } else {
            r.delta = sec;
          }
        } else if (!isZeroBasedPricing && allPrimaryValuesBelowTarget) {
          // When hundred-based and ALL rates are above target (none eligible):
          // For target rate: use rate directly to find closest rate above target
          // For target price: use negative primary value to prioritize highest value
          if (!target.isTargetPrice) {
            r.delta = prim; // Closest rate above target
          } else {
            r.delta = -prim;
          }
        } else if (allPrimaryValuesAboveTarget) {
          // When zero-based pricing is TRUE and ALL primary values are above target: pick closest price above target as eligible
          r.delta = prim;
        } else {
          r.delta = prim + 100;
        }
        if (r.delta < od) {
          hRate[prog.programID as number] = r;
        }
      }
    }
  });

  // update the programs w/ deltas
  const l = aProgs.length;
  let p: SortableLoanProgram;
  for (let i = 0; i < l; i += 1) {
    p = aProgs[i];
    const d = hRate[p.programID as number];
    if (d) {
      p.delta = d.delta;
      p.target = d;
      d.discountPoints = parseNumber((d.price as number) * -1, null);
      Object.assign(p, d);
      if (target.isTargetPrice) {
        p.tPrimary = d.price as number;
        p.tSecondary = d.rate as number;
      } else {
        p.tPrimary = d.rate as number;
        p.tSecondary = d.price as number;
      }
    } else p.delta = 999;
  }

  // sort programs array
  return aProgs.sort(makeSortDelta(isZeroBasedPricing, target.isTargetPrice));
}

export function calcDeltasBestEx(
  aGroups: SortableProductGroup[],
  aRatesBX: any,
  target: Target,
  isZeroBasedPricing: boolean,
): SortableProductGroup[] {
  // get the best delta for each rate
  const hRate: Record<string, SortableRateDetails> = {};
  const l = aRatesBX.length;
  const v = Number(target.value ?? 0);
  const t = target.isTargetPrice
    ? isZeroBasedPricing && v === 100
      ? 0
      : 100 - v
    : v;
  let od: number;
  let r: SortableRateDetails;
  let prim: number;
  let sec: number;
  for (let i = 0; i < l; i += 1) {
    r = aRatesBX[i];
    od = hRate[r.productGroupID as number]?.delta ?? 999;
    prim = (target.isTargetPrice ? r.price : r.rate) as number;
    sec = (target.isTargetPrice ? r.rate : r.price) as number;

    // Eligibility check: rate/price meets target threshold
    // For target rate (both zero and hundred based): rate <= target is eligible
    // For target price zero-based: price <= target is eligible (t - prim >= 0)
    // For target price hundred-based: price >= target is eligible (prim >= t)
    const isEligible = target.isTargetPrice
      ? isZeroBasedPricing
        ? t - prim >= 0
        : prim >= t
      : t - prim >= 0; // For target rate: rate <= target (same as legacy)

    if (isEligible) {
      // For hundred-based target rate: use negative price so highest price wins (min delta)
      // For all other cases: use secondary value directly
      if (!target.isTargetPrice && !isZeroBasedPricing) {
        r.delta = -sec; // Negative price: higher price = lower delta = wins
      } else {
        r.delta = sec;
      }
    } else {
      r.delta = prim + 100;
    }
    if (r.delta < od) {
      hRate[r.productGroupID as number] = r;
    }
  }
  // update the product groups w/ deltas
  aGroups.forEach((g) => {
    const group = g; // Reference to g
    r = hRate[g.productGroupID as number];
    if (r) {
      group.delta = r.delta;
      group.target = r;
      if (target.isTargetPrice) {
        group.tPrimary = r.price as number;
        group.tSecondary = r.rate as number;
      } else {
        group.tPrimary = r.rate as number;
        group.tSecondary = r.price as number;
      }
    } else group.delta = 999;
    // index the rates for expanding/collapsing
    if (group.target !== undefined && Array.isArray(group.rateDetails)) {
      for (let i = 0; i < group.rateDetails.length; i += 1) {
        const row = group.rateDetails[i] as SortableRateDetails;
        if (row) row.indexRow = i;
      }
    }
  });
  return aGroups;
}
